<script setup lang="ts">
import { computed } from 'vue'

import { BoxComponent, FlexComponent, LoadingComponent, TypographyComponent } from '../../'
import type { GetSegmentCreatorLink, SegmentCreator } from '../../../types'

import { SEGMENT_CREATOR_SOURCE_LABELS, SEGMENT_CREATOR_SOURCE_ORDER } from './SegmentPreview.helpers'
import SegmentPreviewGroupHeading from './SegmentPreviewGroupHeading.vue'
import SegmentPreviewListItem from './SegmentPreviewListItem.vue'

const props = withDefaults(
  defineProps<{
    creators: SegmentCreator[]
    isLoading?: boolean
    isRemovable?: boolean
    getCreatorLink?: GetSegmentCreatorLink | null
    isSearchState?: boolean
    emptyStateMsg?: string
    searchStateMsg?: string
  }>(),
  {
    isLoading: false,
    isRemovable: false,
    getCreatorLink: null,
    isSearchState: false,
    emptyStateMsg: 'No creators in this segment yet',
    searchStateMsg: 'No creators match your search',
  },
)
const emits = defineEmits<{ remove: [creator: SegmentCreator] }>()

const sections = computed(() => {
  const flat = [{ key: 'all', label: null, creators: props.creators }]

  const groups = SEGMENT_CREATOR_SOURCE_ORDER.map((source) => ({
    key: source as string,
    label: SEGMENT_CREATOR_SOURCE_LABELS[source],
    creators: props.creators.filter((creator) => creator.source === source),
  })).filter((section) => section.creators.length > 0)

  return groups.length > 1 ? groups : flat
})
</script>

<template>
  <LoadingComponent v-if="isLoading" :size="48" class="segment-preview-list__loading" />

  <BoxComponent v-else-if="creators.length === 0" :pl="16" :pr="16" :pt="48" :pb="48">
    <TypographyComponent variant="body-large" color="var(--secondary-48)" text-align="center">
      {{ isSearchState ? searchStateMsg : emptyStateMsg }}
    </TypographyComponent>
  </BoxComponent>

  <FlexComponent v-else direction="column" :gap="8">
    <template v-for="section in sections" :key="section.key">
      <SegmentPreviewGroupHeading v-if="section.label" :label="section.label" />
      <SegmentPreviewListItem
        v-for="creator in section.creators"
        :key="creator.id"
        :creator="creator"
        :is-removable="isRemovable"
        :get-creator-link="getCreatorLink"
        @remove="emits('remove', $event)"
      />
    </template>
  </FlexComponent>
</template>

<style scoped lang="scss">
.segment-preview-list__loading {
  min-height: 200px;
}
</style>
